/**
 * Dashboard AuthContext — thin wrapper around the shared AppAuthProvider.
 *
 * Provides the same `useAuth()` API surface that all Dashboard consumers
 * already depend on, backed by the battle-tested auth lifecycle in
 * `@airclou/auth-react`.
 *
 * Dashboard-specific logic:
 *   - After fetching the base user from Auth, calls the Dashboard backend's
 *     `getUserContext()` to resolve tenant role / permissions / status.
 *   - Preserves the `redirectToAuth` helper for `AuthGuard`.
 */

import { useMemo, type ReactNode } from 'react'
import {
  AppAuthProvider,
  recoverRefusedToken,
  useAppAuth,
  type AppAuthConfig,
  type AppAuthContextValue,
} from '@airclou/auth-react'
import { dashboardApiClient } from '../services/api'
import { getServiceUrl, getServiceApiUrl } from '@airclou/utils'
import { DASHBOARD_SCOPE_KEYS } from '../utils/scopeKeys'
import { HTTP_TIMEOUT_MS } from '../config'

// ---------------------------------------------------------------------------
// User type — matches what consumers expect
// ---------------------------------------------------------------------------

interface User {
  id: string
  email: string
  name: string
  avatar?: string
  /**
   * The dashboard APP-ACCESS role from `app_access_overrides`, despite the
   * org-role-looking union. It is NOT what the backend gates on, and in
   * practice it never says 'owner' — signup writes 'admin' there. Use `orgRole`
   * for anything about ownership or org-level permission.
   */
  role: 'owner' | 'admin' | 'billing_admin' | 'viewer'
  /**
   * The org role from `tenant_users` — what `requireTenantRole` resolves.
   *
   * Three states, and `null` is not `undefined`. A string is the role. `null`
   * means the context call answered and this user has no `tenant_users` row.
   * `undefined` means we never got an answer.
   */
  orgRole?: 'owner' | 'admin' | 'billing_admin' | 'viewer' | null
  tenantId: string
  tenantName: string
  status: string
  emailVerified: boolean
  permissions: string[]
}

// ---------------------------------------------------------------------------
// Re-export the original context type so existing imports keep working
// ---------------------------------------------------------------------------

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithPasskey: () => Promise<void>
  loginWithSSO: (provider: string) => Promise<void>
  logout: () => void
  register: (email: string, password: string, name: string) => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  acceptInvite: (token: string) => Promise<void>
  redirectToAuth: () => void
  refreshUser: () => Promise<void>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'dashboard:authToken'

/**
 * Where a signed-in person with no organization goes: Auth's sign-in page,
 * which explains why and, on sign-in, shows their options (request access to
 * their company's organization, or use a work email).
 *
 * Dashboard used to quietly create a fresh organization here instead, so
 * someone removed from their company's organization got a new one with a new
 * trial just by opening Dashboard. Organizations are now created only by Auth's
 * signup (docs/plans/one-organization-per-domain-plan.md, OD-3).
 */
function sendToNoOrganizationPage(): void {
  window.location.href = `${getServiceUrl('auth')}/login?app=dashboard&reason=no_organization`
}

/**
 * Call Dashboard backend to get the tenant context.
 *
 * Only NO_ORGANIZATION — no membership in any live organization, judged from
 * tenant_users as Auth does — goes to the no-organization page. TENANT_NO_ACCESS means the person is
 * in an organization but has no Dashboard access (it was revoked); sending them
 * to that page would loop, because Auth signs them straight back in here. That
 * case is turned into an explicit `no_access` status, which AuthGuard shows as
 * the access-denied page.
 */
export async function ensureTenantAccess(): Promise<any> {
  try {
    return await dashboardApiClient.getUserContext()
  } catch (error: any) {
    if (error?.code === 'NO_ORGANIZATION') {
      sendToNoOrganizationPage()
      // The tab is on its way to Auth. Never settle: the API error's numeric
      // status would otherwise be read as a user status and paint the
      // access-denied page for the second before the login page loads.
      return new Promise<never>(() => {})
    } else if (error?.code === 'TENANT_NO_ACCESS') {
      const noAccess = new Error('No Dashboard access')
      ;(noAccess as any).status = 'no_access'
      ;(noAccess as any).code = 'TENANT_NO_ACCESS'
      throw noAccess
    } else if (error?.response?.status === 403) {
      const errorData = error.response?.data || error.response
      const accessDeniedError = new Error('Access denied')
      ;(accessDeniedError as any).status = errorData.status || 'unknown'
      ;(accessDeniedError as any).code = errorData.code || 'ACCESS_DENIED'
      ;(accessDeniedError as any).response = error.response
      throw accessDeniedError
    } else {
      throw error
    }
  }
}
/**
 * Read the org role out of a /context response, keeping "no role" and "no
 * answer" apart.
 *
 * The backend sends `orgRole: null` for a user with no `tenant_users` row, and
 * provisioning paths that write app access without one are real — the users
 * route does it. That null
 * is a settled answer, not a failure. Collapsing it into `undefined` told those
 * people "we could not confirm your role, refresh to try again" on their own
 * account page, forever, because the refresh returned the same null.
 *
 * Presence of the key is what separates the two, so a backend that predates the
 * field reads as unresolved rather than as "no role".
 */
export function resolveOrgRole(ctxUser: any): User['orgRole'] {
  if (!ctxUser || !('orgRole' in ctxUser)) return undefined
  return ctxUser.orgRole ?? null
}

/**
 * Is this context failure an answer about access, rather than a failure to get
 * one? `ensureTenantAccess` turns TENANT_NO_ACCESS into an error with the
 * string status `no_access`; HTTP errors carry a numeric one. A raw 403 is not
 * counted: this route never sends one, so it is infrastructure (a WAF, a proxy)
 * and is retried like any other failure rather than shown as access denied.
 */
function isAccessAnswer(error: any): boolean {
  return typeof error?.status === 'string'
}

/**
 * The token the last /auth/me recovery produced. If /auth/me refuses that one
 * too, renewing again cannot help — something other than staleness is wrong —
 * so the 401 stands. Without this, each of the provider's retries would mint
 * another token before ending in a sign-out anyway.
 */
let meRecoveredToken: string | null = null

/**
 * A 401 from /auth/me, before it is treated as a sign-out.
 *
 * Returns when the 401 stands (the caller throws it, and the provider signs
 * out). Throws a retryable error — no 401 status — when the session may still
 * be live: renewed from the cookie (the provider's retry then uses the new
 * token), or no answer from Auth.
 *
 * No IDENTITY_MISMATCH branch: this request carries no cookie, so Auth has
 * only the one credential and cannot answer with it.
 */
async function recoverRefusedMe(token: string): Promise<void> {
  if (token === meRecoveredToken) return
  const outcome = await recoverRefusedToken(token)
  if (outcome === 'renewed') {
    meRecoveredToken = localStorage.getItem(TOKEN_KEY)
    throw new Error('Session token renewed; retrying')
  }
  if (outcome === 'unreachable') throw new Error('Could not renew the session right now')
  // `ended` (the provider is signing out with its reason), `refused`, or no
  // provider: the 401 stands.
}

// ---------------------------------------------------------------------------
// App-specific config for the shared provider
// ---------------------------------------------------------------------------

/** Exported for the test that pins how `fetchUser` reports a failure. */
export const dashboardAuthConfig: AppAuthConfig = {
  appName: 'dashboard',

  getToken: () => localStorage.getItem(TOKEN_KEY),

  setToken: (token: string) => {
    localStorage.setItem(TOKEN_KEY, token)
    dashboardApiClient.setToken(token)
  },

  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY)
    dashboardApiClient.clearToken()
  },

  fetchUser: async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return null

    dashboardApiClient.setToken(token)

    // 1. Fetch base user from Auth service
    const authApiUrl = getServiceApiUrl('auth')
    const response = await fetch(`${authApiUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    })

    if (!response.ok) {
      // A refused token may only be stale — a laptop that slept through a
      // renewal — while the cookie session is live. Renew it from the cookie
      // before treating the 401 as a sign-out.
      if (response.status === 401) await recoverRefusedMe(token)
      // Throw with the status either way. The shared provider retries a thrown
      // non-401 with backoff and keeps the token; a `null` return it reads as
      // "not signed in, do not retry" and sends the person to the login page —
      // so a database blip behind /auth/me (now a 500, not a 401) would still
      // have bounced them out.
      const err = new Error(response.status === 401 ? 'Unauthorized' : `Failed to load user (${response.status})`) as any
      err.statusCode = response.status
      throw err
    }

    const result = await response.json()
    // Support both { success, data: { user } } (new) and { success, user } (legacy)
    const user = result.data?.user ?? result.user
    if (!user) return null

    // 2. Resolve tenant context from Dashboard backend
    try {
      const ctx = await ensureTenantAccess()
      // The context call can renew the token from the cookie on its way, and
      // the cookie may name another account than the one /auth/me answered for.
      // Never build one user out of two; retry, and both calls agree next time.
      if (ctx?.user?.id && ctx.user.id !== user.id) {
        throw new Error('The session changed account while loading; retrying')
      }
      return {
        id: user.id,
        email: user.email,
        name: user.name,
        avatar: user.avatar_url || user.avatar,
        role: ctx?.user?.role || user.role || 'viewer',
        // The ORG role from `tenant_users`, which `role` above is not: that one
        // is the dashboard app-access role and can never say `owner`. Anything
        // gating on ownership must read this. Either way an owner-only control
        // stays hidden unless this says `owner`, rather than being shown on a
        // guess.
        orgRole: resolveOrgRole(ctx?.user),
        tenantId: ctx?.tenant?.id || 'unknown',
        tenantName: ctx?.tenant?.name || 'Unknown Organization',
        status: ctx?.user?.status || user.status || 'active',
        emailVerified: user.email_verified || false,
        permissions: ctx?.user?.permissions || user.permissions || ['read'],
      } as User
    } catch (ctxError: any) {
      // An answer about access: return the user with that status, which
      // AuthGuard turns into the access-denied page.
      if (isAccessAnswer(ctxError)) {
        return {
          id: user.id,
          email: user.email,
          name: user.name,
          avatar: user.avatar_url || user.avatar,
          role: user.role || 'viewer',
          tenantId: 'unknown',
          tenantName: 'Unknown Organization',
          status: ctxError.status || user.status || 'active',
          emailVerified: user.email_verified || false,
          permissions: user.permissions || ['read'],
        } as User
      }
      // The token was refused and renewing it from the cookie could not help
      // (the API client already tried). The provider clears it and signs out.
      // Not IDENTITY_MISMATCH: the provider is re-deriving, so that one retries.
      if (ctxError?.statusCode === 401 && ctxError?.code !== 'IDENTITY_MISMATCH') throw ctxError
      // Anything else is a failure to get an answer — a timeout, a 500, a
      // network drop. This used to return the user without an org role, which
      // the provider took as success: Settings and Billing vanished from the
      // sidebar until the next reload, with nothing logged. Throwing (without a
      // 401 status) lets the provider retry with backoff instead, keeping the
      // spinner up on first load and the current user on a refresh.
      const err = new Error(`Failed to load organization context: ${ctxError?.message ?? ctxError}`) as any
      err.cause = ctxError
      throw err
    }
  },

  // `reason` tells the login page why it is being shown. The per-tab inactivity
  // logout used to build this URL itself; now the server decides a session is idle
  // and the provider passes the reason through, so an unexplained bounce to a login
  // form becomes "you were signed out for inactivity" (I-11).
  onLogout: (reason) => {
    // `reason` tells the login page why it is being shown, and `redirect` returns
    // the user to where they were. Both came from the per-tab inactivity handler
    // this replaced; dropping either turns an idle sign-out into an unexplained
    // bounce that also loses the page. A *manual* sign-out deliberately carries
    // neither — someone who chose to leave should land on a clean login form, not be
    // sent back to the page they just left.
    const involuntary = reason === 'inactivity' || reason === 'revoked';
    const why = reason === 'inactivity' ? '&reason=inactivity' : '';
    const back = involuntary ? `&redirect=${encodeURIComponent(window.location.href)}` : '';
    const authServiceUrl = getServiceUrl('auth')
    window.location.href = `${authServiceUrl}/login?app=dashboard${why}${back}`
  },

  skipDemoTokens: false,

  extraKeysToClearOnTenantSwitch: DASHBOARD_SCOPE_KEYS,
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <AppAuthProvider config={dashboardAuthConfig}>
      {children}
    </AppAuthProvider>
  )
}

// ---------------------------------------------------------------------------
// Hook — maps the shared context to Dashboard's existing API surface
// ---------------------------------------------------------------------------

function redirectToAuth() {
  const currentUrl = new URL(window.location.href)
  currentUrl.searchParams.delete('token')
  const redirectUrl = encodeURIComponent(currentUrl.toString())
  window.location.href = `${getServiceUrl('auth')}/login?app=dashboard&redirect=${redirectUrl}`
}

export function useAuth(): AuthContextType {
  const shared: AppAuthContextValue = useAppAuth()

  return useMemo<AuthContextType>(
    () => ({
      user: shared.user as User | null,
      token: localStorage.getItem(TOKEN_KEY),
      isLoading: shared.isLoading,
      // These auth actions are handled by the Auth service — stubs for type compat
      login: async () => { redirectToAuth() },
      loginWithPasskey: async () => { redirectToAuth() },
      loginWithSSO: async () => { redirectToAuth() },
      register: async () => { redirectToAuth() },
      verifyEmail: async () => { /* handled by auth service */ },
      acceptInvite: async () => { /* handled by auth service */ },
      redirectToAuth,
      logout: shared.logout,
      refreshUser: shared.refreshUser,
    }),
    [
      shared.user,
      shared.isLoading,
      shared.logout,
      shared.refreshUser,
    ],
  )
}
